import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { BrowserSize, BrowserWrapper } from "@/Apps/Pages/Editor/BrowserWrapper";
import { MobileState } from "@/Apps/Pages/MobileResponsiveToggle/types";
import { watchFrameSelection } from "./Notes";
import { pageParts, sectionOptions, sectionsOf, wordDiff, type Item, type Sections, type Version } from "./content";

// RV-1 — how an item looks in the room: as a real post, an email, or plain text, with the
// chosen version or tracked changes between two versions (green added, red removed).

export type Plat = "fb" | "ig" | "li";
export const PLATFORMS: { key: Plat; label: string; badge: string }[] = [
  { key: "fb", label: "Facebook", badge: "f" },
  { key: "ig", label: "Instagram", badge: "IG" },
  { key: "li", label: "LinkedIn", badge: "in" },
];

// [bracketed] notes ("[confirm …]") stand out, as in the staging reviews
function Brackets({ text }: { text: string }) {
  const parts = text.split(/(\[[^\]]{3,}\])/);
  return <>{parts.map((p, i) => (/^\[[^\]]{3,}\]$/.test(p) ? <mark key={i}>{p}</mark> : <Fragment key={i}>{p}</Fragment>))}</>;
}

// a block of text: either one version, or the changes from `base` to `text`
export function Text({ text = "", base }: { text?: string; base?: string }) {
  if (base === undefined) return <Brackets text={text} />;
  return (
    <>
      {wordDiff(base, text).map((sg, i) =>
        sg.t === "ins" ? <ins key={i}><Brackets text={sg.s} /></ins> : sg.t === "del" ? <del key={i}>{sg.s}</del> : <Brackets key={i} text={sg.s} />,
      )}
    </>
  );
}

function Graphic({ sections, kind, ig }: { sections: Sections; kind?: string; ig?: boolean }) {
  const lines = (sections.graphic || "").split("\n").map((l) => l.trim()).filter(Boolean);
  const foot = lines.find((l) => /MemberFDIC|Equal Housing/i.test(l)) || "";
  const body = lines.filter((l) => l !== foot);
  if (kind === "Reels" && sections.reel) {
    const scenes = sections.reel.split("\n").filter((l) => l.trim());
    return (
      <div className={`lr-graphic reel ${ig ? "ig" : ""}`}>
        <span className="g-note">REEL · script</span>
        <div className="g-title">{body[0] || "Reel"}</div>
        <div className="scenes">{scenes.slice(0, 8).map((s, i) => <div key={i}>{s}</div>)}</div>
        {foot && <div className="g-foot">{foot}</div>}
      </div>
    );
  }
  if (!body.length) return null;
  return (
    <div className={`lr-graphic ${ig ? "ig" : ""}`}>
      <span className="g-note">GRAPHIC · text only</span>
      <div className="g-title">{body[0]}</div>
      {body.slice(1, 4).map((l, i) => <div className="g-line" key={i}>{l}</div>)}
      {foot && <div className="g-foot">{foot}</div>}
    </div>
  );
}

const linkIn = (t = "") => (/(\b[a-z0-9-]+\.(?:net|com|org|bank)\/\S*|https?:\/\/\S+)/i.exec(t) || [])[0];

export function SocialPost({ plat, v, base, kind, handle = "account" }: { plat: Plat; v: Version; base?: Version; kind?: string; handle?: string; name?: string; date?: string }) {
  const s = sectionsOf(v.body) || {};
  const b = base ? sectionsOf(base.body) || {} : undefined;
  const text = s[plat] || "";
  const link = linkIn(text);
  if (plat === "ig") {
    return (
      <article className="lr-post">
        <Graphic sections={s} kind={kind} ig />
        <div className="igacts" aria-hidden="true">♡ 💬 ↗</div>
        <div className="text"><span className="handle">{handle}</span><Text text={text} base={b ? b.ig || "" : undefined} /></div>
      </article>
    );
  }
  return (
    <article className="lr-post">
      <div className="text"><Text text={text} base={b ? b[plat] || "" : undefined} /></div>
      <Graphic sections={s} kind={kind} />
      {link && <div className="link-card">{link}</div>}
      <div className="acts" aria-hidden="true"><span>👍 Like</span><span>💬 Comment</span><span>↗ {plat === "li" ? "Repost" : "Share"}</span></div>
    </article>
  );
}

export function EmailView({ v, base }: { v: Version; base?: Version }) {
  return (
    <div className="lr-email">
      {v.sender && <div className="from">From {v.sender}</div>}
      <div className="subj"><Text text={v.subject || ""} base={base ? base.subject || "" : undefined} /></div>
      {(v.preview || base?.preview) && <div className="prev"><Text text={v.preview || ""} base={base ? base.preview || "" : undefined} /></div>}
      <div className="lr-ebody"><Text text={v.body || ""} base={base ? base.body || "" : undefined} /></div>
    </div>
  );
}

export function PlainView({ v, base }: { v: Version; base?: Version }) {
  return <div className="lr-email"><div className="lr-ebody" style={{ borderTop: 0, paddingTop: 0 }}><Text text={v.body || ""} base={base ? base.body || "" : undefined} /></div></div>;
}

// a drawn page's wording, compared slot by slot ("Headline: …" against "Headline: …")
const slots = (t: string) => { const m = new Map<string, string>(); for (const line of t.split("\n")) { const x = /^([^:]{1,40}):\s?(.*)$/.exec(line); if (x) m.set(x[1], x[2]); } return m; };
function SlotChanges({ now, before }: { now: string; before: string }) {
  const a = slots(before), b = slots(now);
  const keys = [...b.keys(), ...[...a.keys()].filter((k) => !b.has(k))];
  return <div className="slotdiff">{keys.map((k) => <div key={k}><span className="k">{k}</span><span className="pgtext"><Text text={b.get(k) || ""} base={a.get(k) || ""} /></span></div>)}</div>;
}

// RV-6 — Page room: the page in the same browser frame and device switch Launch Pages and Launch
// Sites use (BrowserWrapper + MobileResponsiveToggle), at its natural width; who sees it above,
// and the email that follows it in an inbox frame. A page with real HTML shows that HTML, sandboxed
// (no scripts), grown to its full height so it reads like the live page.
const pageUrl = (site = "") => `${site.toLowerCase().replace(/[^a-z0-9]+/g, "") || "yoursite"}.com`;

function FullPage({ html, title }: { html: string; title: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [h, setH] = useState(900);
  useEffect(() => {
    const f = ref.current;
    if (!f) return;
    let ro: ResizeObserver | null = null;
    const fit = () => { const d = f.contentDocument; if (d?.documentElement) setH(Math.max(400, d.documentElement.scrollHeight)); };
    let unwatch = () => {};
    const onLoad = () => { fit(); unwatch = watchFrameSelection(f); const d = f.contentDocument; if (d?.body && "ResizeObserver" in window) { ro = new ResizeObserver(fit); ro.observe(d.body); } };
    f.addEventListener("load", onLoad);
    // a small page can finish loading before this runs; catch it up
    if (f.contentDocument?.readyState === "complete" && f.contentDocument.body?.childElementCount) onLoad();
    return () => { f.removeEventListener("load", onLoad); ro?.disconnect(); unwatch(); };
  }, [html]);
  return <iframe ref={ref} title={title} sandbox="allow-same-origin" srcDoc={html} style={{ display: "block", width: "100%", height: h, border: 0, background: "#fff" }} />;
}

export function PageView({ v, base, site, label, device = { type: MobileState.DESKTOP } }: { v: Version & { html?: string; url?: string }; base?: Version; site?: string; label?: string; device?: { type: MobileState } }) {
  const p = pageParts(v.body), b = base ? pageParts(base.body) : undefined;
  const url = `${v.url || pageUrl(site)}${label && label !== v.url ? ` · ${label}` : ""}`;
  return (
    <div className="lr-page">
      {p.audience && <div className="who"><b>Shown to:</b> <Text text={p.audience} base={b?.audience} /></div>}
      <div className="lr-browser">
        <BrowserWrapper mobileState={device} size={BrowserSize.LARGE} url={url} withPreview={false}>
          {v.html && base ? (
            <div className="pg"><div className="pgnote">The wording changes, slot by slot. Pick a version to see it drawn as the page.</div><SlotChanges now={v.body || ""} before={base.body || ""} /></div>
          ) : v.html ? (
            <FullPage html={v.html} title={label || "Page"} />
          ) : (
            <div className="pg">
              {p.headline && <h2><Text text={p.headline} base={b?.headline} /></h2>}
              <div className="pgtext"><Text text={p.screen} base={b?.screen} /></div>
            </div>
          )}
        </BrowserWrapper>
      </div>
      {p.email && (
        <div className="lr-email lr-follow">
          <div className="from">{p.emailLabel || "The email that follows"}</div>
          <div className="lr-ebody"><Text text={p.email} base={b?.email} /></div>
        </div>
      )}
    </div>
  );
}

// RV-6 — a copy-room page redrawn live with sections mixed. The room's own page code runs inside a
// sandboxed frame (scripts only: no access to the app, its cookies or its storage); the room sends
// it what to draw. Inside, each part of the page (headline, bullets, button…) outlines on hover and
// a click asks the room to offer that part's options; selected words still go to comments.
export type RoomDraw = { code: string; css: string; vars: string; fields: unknown[]; slots: [string, string][]; cfgs: Record<string, Record<string, unknown>> };
// where each part sits in the copy room's page template (the shell's lp() output)
const PARTS: Record<string, string> = { eyebrow: ".lp-col.left .f-eyebrow", headline: ".f-title", kicker: ".f-kicker", bullets: ".f-list", testimonial: ".f-proof", formEyebrow: ".lp-col.right .f-eyebrow", formHeadline: ".f-form-title", formIntro: ".f-copy", cta: ".f-btn", footer: ".f-fine" };
const safe = (t: string) => t.replace(/<\/script/gi, "<\\/script").replace(/<!--/g, "<\\!--");
function MixedPage({ room, cfg, switched, title, onPart }: { room: RoomDraw; cfg: Record<string, unknown>; switched: Record<string, string>; title: string; onPart: (slot: string, at: { x: number; y: number }) => void }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [h, setH] = useState(900);
  const srcDoc = useMemo(() => `<!doctype html><html><head><meta charset="utf-8"><style>${room.css}</style><style>:root{${room.vars}}body{margin:0;background:var(--b-ground,#fff)}
[data-lr-slot]{cursor:pointer;outline:2px dashed transparent;outline-offset:6px;border-radius:4px;transition:outline-color .15s}
[data-lr-slot]:hover{outline-color:rgba(96,165,250,.95)}
[data-lr-switched]{outline:2px solid rgba(16,185,129,.9)}</style></head><body><div id="r"></div><script>
const FIELDS=${safe(JSON.stringify(room.fields))};const SLOTS=${safe(JSON.stringify(room.slots))};const PARTS=${safe(JSON.stringify(PARTS))};
${safe(room.code)}
let last=0,SW={};const fit=()=>{const x=document.documentElement.scrollHeight;if(x!==last){last=x;parent.postMessage({lr:"h",h:x},"*")}};
const tag=()=>{for(const k in PARTS){const el=document.querySelector(PARTS[k]);if(!el)continue;el.setAttribute("data-lr-slot",k);el.title="Click to see the other options";if(SW[k])el.setAttribute("data-lr-switched","");else el.removeAttribute("data-lr-switched")}};
addEventListener("message",(e)=>{if(e.data&&e.data.lr==="cfg"){document.getElementById("r").innerHTML=lp(e.data.cfg);SW=e.data.sw||{};tag();fit()}});
new ResizeObserver(fit).observe(document.body);
document.addEventListener("mouseup",()=>setTimeout(()=>{const s=getSelection();const q=String(s).replace(/\\s+/g," ").trim();if(q.length<3||!s.rangeCount)return;const r=s.getRangeAt(0).getBoundingClientRect();parent.postMessage({lr:"sel",q,x:r.left+r.width/2,y:r.top},"*")},10));
document.addEventListener("click",(e)=>{if(String(getSelection()).trim().length>2)return;const el=e.target.closest("[data-lr-slot]");if(!el)return;e.preventDefault();const r=el.getBoundingClientRect();parent.postMessage({lr:"part",slot:el.getAttribute("data-lr-slot"),x:r.left,y:r.bottom},"*")});
parent.postMessage({lr:"ready"},"*");
</script></body></html>`, [room]);
  const send = () => ref.current?.contentWindow?.postMessage({ lr: "cfg", cfg, sw: switched }, "*");
  useEffect(() => {
    const on = (e: MessageEvent) => {
      const f = ref.current;
      if (!f || e.source !== f.contentWindow || !e.data || typeof e.data !== "object") return;
      const r = f.getBoundingClientRect();
      if (e.data.lr === "ready") send();
      else if (e.data.lr === "h" && typeof e.data.h === "number") setH(Math.max(400, Math.min(20000, e.data.h)));
      else if (e.data.lr === "sel" && typeof e.data.q === "string") window.dispatchEvent(new CustomEvent("lr-select", { detail: { quote: e.data.q.slice(0, 1000), x: r.left + Number(e.data.x || 0), y: r.top + Number(e.data.y || 0), frame: f } }));
      else if (e.data.lr === "part" && typeof e.data.slot === "string" && PARTS[e.data.slot]) onPart(e.data.slot, { x: r.left + Number(e.data.x || 0), y: r.top + Number(e.data.y || 0) });
    };
    window.addEventListener("message", on);
    return () => window.removeEventListener("message", on);
  });
  useEffect(send, [cfg, switched]);
  return <iframe ref={ref} title={title} sandbox="allow-scripts" srcDoc={srcDoc} style={{ display: "block", width: "100%", height: h, border: 0, background: "#fff" }} />;
}

// which page section a "Headline: …" line belongs to (the room's slot key)
const slotKeyOf = (room: RoomDraw, label: string) => (room.slots.find(([k, l]) => l === label || k.toLowerCase() === label.toLowerCase()) || [label.toLowerCase()])[0];

// RV-6 — Page room, copy-room pages. The whole version is the main choice ("the option is the
// whole set"): a version switch sits above the page. Fine-tuning comes second: click any part of
// the page (headline, bullets, button…) to swap just that part from another version; the full list
// of sections lives in the side panel (SectionList). Each page starts from its version; swaps sit on
// top; picking another version starts the page fresh. Mixing is allowed, with a warning.
export const swapsOf = (picks: Record<string, string>, base: string) => Object.fromEntries(Object.entries(picks).filter(([, k]) => k !== base));
export function mixCfg(room: RoomDraw, base: string, picks: Record<string, string>) {
  const c: Record<string, unknown> = { ...room.cfgs[base], raw: false };
  for (const [label, key] of Object.entries(picks)) {
    const from = room.cfgs[key];
    if (!from) continue;
    const slot = slotKeyOf(room, label);
    if (slot === "testimonial") { c.t = from.t || null; c.tNote = from.tNote; } else c[slot] = from[slot];
  }
  return c;
}

export function SectionsView({ item, order, labels, base, picks, onBase, onPick, onReset, device, url }: { item: Item & { room?: RoomDraw }; order: string[]; labels: Record<string, string>; base: string; picks: Record<string, string>; onBase: (key: string) => void; onPick: (section: string, key: string) => void; onReset: () => void; device?: { type: MobileState }; url?: string }) {
  const rows = sectionOptions(item, order);
  const room = item.room;
  const versions = order.filter((k) => room?.cfgs[k]);
  const [menu, setMenu] = useState<{ slot: string; x: number; y: number } | null>(null);
  const swaps = swapsOf(picks, base);
  const cfg = useMemo(() => (room ? mixCfg(room, base, swaps) : {}), [room, base, picks]);
  const switched = useMemo(() => (room ? Object.fromEntries(Object.entries(swaps).map(([l, k]) => [slotKeyOf(room, l), k])) : {}), [room, base, picks]);
  const n = Object.keys(swaps).length;
  const used = new Set([base, ...Object.values(swaps)].filter((k) => k !== "today"));
  useEffect(() => { if (!menu) return; const close = (e: KeyboardEvent) => e.key === "Escape" && setMenu(null); window.addEventListener("keydown", close); return () => window.removeEventListener("keydown", close); }, [menu]);
  const menuRow = menu && room ? rows.find((r) => slotKeyOf(room, r.label) === menu.slot) : null;
  return (
    // clicks here are for the page's own controls, never the card's "open the panel"
    <div className="lr-sections" onClick={(e) => e.stopPropagation()}>
      <div className="vbar">
        <span className="seg" role="group" aria-label="Version of this page">
          {versions.map((k) => <button key={k} aria-pressed={base === k} onClick={() => onBase(k)} title={labels[k] || k}>{k === "today" ? "Today" : labels[k] || k}</button>)}
        </span>
      </div>
      <div className="bar">
        <span>{n ? <><b>{n}</b> part{n === 1 ? "" : "s"} swapped from {base === "today" ? "today's page" : `version ${base}`} <button className="lnk" onClick={onReset}>Reset</button></> : <>Showing {base === "today" ? "today's page" : `version ${base}`}.</>}</span>
        <span className="hint">Click any part of the page to swap just that part.</span>
      </div>
      {used.size > 1 && <div className="lr-mix">This page mixes versions {[...used].join(" and ")}. That's allowed; just check the headline still fits the proof and the button.</div>}
      {room ? (
        <div className="lr-browser">
          <BrowserWrapper mobileState={device || { type: MobileState.DESKTOP }} size={BrowserSize.LARGE} url={url || "•••"} withPreview={false}>
            <MixedPage room={room} cfg={cfg} switched={switched} title={item.title || "Page"} onPart={(slot, at) => setMenu({ slot, ...at })} />
          </BrowserWrapper>
        </div>
      ) : null}
      {menu && menuRow && (
        <div className="lr-partmenu-back" onClick={() => setMenu(null)}>
          <div className="lr-partmenu" role="dialog" aria-label={`${menuRow.label} options`} style={{ left: Math.max(12, Math.min(window.innerWidth - 392, menu.x)), top: Math.min(window.innerHeight - 40, menu.y + 8) }} onClick={(e) => e.stopPropagation()}>
            <div className="h">{menuRow.label}</div>
            {menuRow.options.map((o) => {
              const on = (picks[menuRow.label] || base) === o.key;
              return (
                <button key={o.key} className={`o ${on ? "on" : ""}`} aria-pressed={on} onClick={() => { onPick(menuRow.label, o.key); setMenu(null); }}>
                  <span className="k">{o.key === "today" ? "Today" : labels[o.key] || o.key}{on ? " · showing" : o.key === base ? " · this page's version" : ""}</span>
                  <span className="v">{o.text.replace(/~/g, "")}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// the side panel's list of every section, for swapping parts without hunting on the page
export function SectionList({ item, order, base, picks, onPick }: { item: Item; order: string[]; base: string; picks: Record<string, string>; onPick: (section: string, key: string) => void }) {
  const rows = sectionOptions(item, order);
  return (
    <div className="lr-seclist">
      <div className="h">Sections <span>· swap one part at a time</span></div>
      {rows.map((r) => (
        <div className="row" key={r.label}>
          <span className="lab" title={r.label}>{r.label}</span>
          <span className="chips" role="group" aria-label={r.label}>
            {r.options.map((o) => {
              const on = (picks[r.label] || base) === o.key;
              return <button key={o.key} aria-pressed={on} title={o.text.replace(/~/g, "")} className={`${o.key === "today" ? "today" : ""} ${on && o.key !== base ? "swapped" : ""}`} onClick={() => onPick(r.label, o.key)}>{o.key === "today" ? "Now" : o.key}</button>;
            })}
          </span>
        </div>
      ))}
    </div>
  );
}
